import * as R from 'ramda';

/**
 * Classifies a collection of uniform objects as [key, value] entries. This
 * affords sortability.
 *
 * @param  {array} taxonomy - Array of keys to recursively group objects by.
 *   Each value in the array can be either:
 *   - string: Key in each object, the value of which will be used to classify
 *     the object.
 *   - function: Invoked with each object in the collection, the return value
 *     will be used to classify the object.
 *   - array: First element should be a string or key generation function,
 *     second element should be a comparator function for sorting entries.
 * @param  {array} collection - The collection to classify.
 * @return {object}
 */
export function classifyAsEntries(taxonomy, collection) {
  if (!R.is(Array, taxonomy)) {
    throw new Error([
      '[classifyAsEntries] expected first argument to be of type "Array",',
      `but got "${typeof taxonomy}"`
    ].join(' '));
  }

  if (!R.is(Array, collection)) {
    throw new Error([
      '[classifyAsEntries] expected second argument to be of type "Array",',
      `but got "${typeof collection}"`
    ].join(' '));
  }

  if (taxonomy.length) {
    // Current tier is the first item in the taxonomy.
    const tier = R.head(taxonomy);

    const generateKey = item => {
      const keyGenerator = R.is(Array, tier) ? tier[0] : tier;

      if (R.is(String, keyGenerator)) {
        // Use the value of the key for grouping.
        return R.prop(keyGenerator, item);
      } else if (R.is(Function, keyGenerator)) {
        // Use a custom function for grouping.
        return keyGenerator(item);
      } else {
        throw new Error([
          '[classifyByEntries] Expected key descriptor to be of type "String" or',
          `"Function", but got "${typeof keyGenerator}".`
        ].join(' '));
      }
    };

    // Generate entries.
    const entries = R.toPairs(R.map(members => {
      return classifyAsEntries(R.tail(taxonomy), members);
    }, R.groupBy(generateKey, collection)));

    // Optionally sort entries.
    if (R.is(Array, tier) && R.is(Function, tier[1])) {
      const sortByFn = tier[1];
      return R.sort(([a], [b]) => sortByFn(a, b), entries);
    }

    // Return unsorted entries.
    return entries;
  } else {
    return collection;
  }
}


export default classifyAsEntries;



// WEBPACK FOOTER //
// ./src/lib/classifyAsEntries.js